import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, minLength, required } from '@angular/forms/signals';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { Suggestion } from '../../../../core/api/mhsAiToolsApiClient';
import { AitoolsService } from '../../../../core/services/aitools-service';
import { FieldErrorsComponent } from '../../../../shared/ui/components/field-error/field-error';
import { ProcedureSetData } from '../../dto/procedure-set-data';
import { ProcedureSetsService } from '../../services/procedure-sets-service';

@Component({
  selector: 'app-procedure-set-edit',
  imports: [
    FormsModule,
    FormField,
    RouterModule,
    NzIconModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzDividerModule,
    NzCheckboxModule,
    NzSpinModule,
    NzListModule,
    NzCollapseModule,
    NzTypographyModule,
    NzFlexModule,
    NzTagModule,
    FieldErrorsComponent,
  ],
  templateUrl: './procedure-set-edit.html',
  styleUrl: './procedure-set-edit.scss',
})
export class ProcedureSetEditComponent {
  readonly router = inject(Router);
  readonly messageService = inject(NzMessageService);
  readonly procedureSetsService = inject(ProcedureSetsService);
  readonly route = inject(ActivatedRoute);
  readonly aiToolsService = inject(AitoolsService);
  readonly id = this.route.snapshot.paramMap.get('id')
    ? +this.route.snapshot.paramMap.get('id')!
    : (this.route.snapshot.data['id'] as number);
  readonly model = signal<ProcedureSetData>({
    name: '',
    description: '',
    type: '',
    defaultEtcInMinutes: '',
    isHidden: false,
    procedures: [],
    proceduresCount: 0,
  });
  readonly isLoaded = signal(false);
  readonly isSubmitting = signal(false);
  readonly submitted = signal(false);
  readonly isGeneratingSuggestions = signal(false);
  readonly suggestionError = signal<string | null>(null);
  readonly showEmptyState = computed(() => {
    return (
      (this.suggestionError() === null || this.suggestionError() === '') &&
      (this.suggestions() === null || this.suggestions()?.length === 0)
    );
  });
  editForm = form(this.model, (procedureSet) => {
    (required(procedureSet.name),
      minLength(procedureSet.name, 3, { message: 'Nazwa powinna mieć przynajmniej 3 znaki' }));
  });
  suggestions = signal<Suggestion[] | null>(null);

  constructor() {
    this.loadData();
  }

  private loadData(): void {
    this.procedureSetsService.getById(this.id).subscribe((data) => {
      this.model.set({
        name: data.name!,
        description: data.description!,
        type: data.type!,
        defaultEtcInMinutes:
          data.defaultEtcInMinutes === null || data.defaultEtcInMinutes === undefined
            ? ''
            : String(data.defaultEtcInMinutes),
        isHidden: data.isHidden!,
        procedures: data.medServices?.map((p) => p.name!) || [],
        proceduresCount: data.medServices?.length || 0,
      });
      this.isLoaded.set(true);
    });
  }
  async generateSuggestions(): Promise<void> {
    this.isGeneratingSuggestions.set(true);
    this.suggestionError.set(null);
    this.suggestions.set(null);
    try {
      const suggestions = await this.aiToolsService.getMedServiceGroupSuggestions(
        this.model()?.procedures || [],
      );
      this.suggestions.set(suggestions);
    } catch (error) {
      this.suggestionError.set('Failed to generate suggestions.');
    } finally {
      this.isGeneratingSuggestions.set(false);
    }
  }
  setName(name: string): void {
    this.model.update((m) => {
      return { ...m!, name: name };
    });
  }
  submit(): void {
    this.submitted.set(true);
    if (!this.editForm().valid()) {
      this.isSubmitting.set(false);
      return;
    }
    const name = this.model()?.name?.trim();
    const defaultEtcInMinutes = String(this.model()?.defaultEtcInMinutes ?? '').trim();
    if (!name) return;

    this.isSubmitting.set(true);
    this.procedureSetsService.updateMetadata(this.id, name, defaultEtcInMinutes).subscribe({
      next: () => {
        this.messageService.success('Zestaw został zaktualizowany');
        this.router.navigate(['/procedure-sets'], {
          relativeTo: this.route,
        });
      },
      error: () => {
        this.messageService.error('Nie udało się zaktualizować zestawu');
        this.isSubmitting.set(false);
      },
    });
  }
}
